// DD-3735: after a release, a tab still running the old bundle asks for lazy chunks that no longer
// exist and gets "Loading chunk N failed". That is not a bug: reloading fetches the new bundle. The
// tab reloads once instead of reporting; if the chunk still fails right after that reload, the
// deploy itself is broken and the error is reported like any other.

const CHUNK_RELOAD_AT_KEY = 'denthub.chunkErrorReloadAt';
const RELOAD_GUARD_MS = 5 * 60 * 1000;

let isReloadPending = false;

export const isChunkLoadError = (error: unknown): boolean => {
    const name = (error as Error)?.name ?? '';
    const message = (error as Error)?.message ?? (typeof error === 'string' ? error : '');
    return (
        name === 'ChunkLoadError' ||
        /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
        message.includes('Failed to fetch dynamically imported module')
    );
};

/**
 * Reloads the page once for a chunk load error. Returns true when a reload is under way (started now
 * or by an earlier error on this page), false when the error should be handled as usual: the tab
 * already reloaded for a chunk error moments ago, or storage is blocked so a loop cannot be ruled out.
 */
export const reloadOnceForChunkError = (reload: () => void = () => window.location.reload()): boolean => {
    if (isReloadPending) return true;
    try {
        const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_AT_KEY));
        if (lastReloadAt && Date.now() - lastReloadAt < RELOAD_GUARD_MS) return false;
        sessionStorage.setItem(CHUNK_RELOAD_AT_KEY, String(Date.now()));
    } catch {
        return false;
    }
    isReloadPending = true;
    reload();
    return true;
};

/** Handles a chunk error by reloading; true when it did (or one is already under way). */
export const handleChunkLoadError = (error: unknown): boolean => isChunkLoadError(error) && reloadOnceForChunkError();

/** Test helper. */
export const resetChunkReloadState = (): void => {
    isReloadPending = false;
};
